import React, { useState, useCallback } from 'react';
import {
  Button,
  Avatar,
  Container,
  TextInput,
  Textarea,
  MultiSelect,
  Box,
  Stack,
  Title,
  Paper,
  Grid,
  Badge,
  Group,
  Text,
} from '@mantine/core';
import { Notifications, showNotification } from '@mantine/notifications';
import { IconCheck, IconAlertCircle, IconEdit, IconArrowBack } from '@tabler/icons-react';
import ImageUploadModal from '../components/ImageUploadModal';

// Skip type definitions to focus on the core issue

interface UserAccountState {
  isEditMode: boolean;
  username: string;
  email: string;
  bio: string;
  avatarPreview: string | null;
  postSeoPreview: string | null;
  role_ids: string[];
  processing: boolean;
  errors: Record<string, string>;
}

interface UserAccountProps {
  available_roles?: string[];
  pageProps?: {
    user?: {
      username?: string;
      email?: string;
      bio?: string;
      avatar_url?: string;
      post_seo_image_url?: string;
      role_ids?: string[];
    };
  };
  initialEditMode?: boolean;
}

class UserAccountComponent extends React.Component<UserAccountProps, UserAccountState> {
  constructor(props) {
    super(props);

    const { user: initialUser } = props;
    const user = props.pageProps?.user || initialUser;

    this.state = {
      isEditMode: props.initialEditMode || false,
      username: user.username || '',
      email: user.email || '',
      bio: user.bio || '',
      avatarPreview: user.avatar_url || null,
      postSeoPreview: user.post_seo_image_url || null,
      role_ids: user.role_ids || [],
      processing: false,
      errors: {},
    };

    // Bind methods
    this.handleUsernameChange = this.handleUsernameChange.bind(this);
    this.handleEmailChange = this.handleEmailChange.bind(this);
    this.handleBioChange = this.handleBioChange.bind(this);
    this.handleRoleChange = this.handleRoleChange.bind(this);
    this.handleAvatarChange = this.handleAvatarChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.enableEditMode = this.enableEditMode.bind(this);
    this.cancelEdit = this.cancelEdit.bind(this);
  }

  // Input handlers with simple setState
  handleUsernameChange(e) {
    this.setState({ username: e.target.value });
  }

  handleEmailChange(e) {
    this.setState({ email: e.target.value });
  }

  handleBioChange(e) {
    this.setState({ bio: e.target.value });
  }

  handleRoleChange(newRoles) {
    this.setState({ role_ids: newRoles });
  }

  handleAvatarChange(file) {
    const preview = URL.createObjectURL(file);
    this.setState({
      avatarFile: file,
      avatarPreview: preview,
    });
  }

  handleSubmit(e) {
    e.preventDefault();
    // Simple alert to confirm form submission works
    alert('Form would be submitted here');
    this.setState({ isEditMode: false });
  }

  enableEditMode() {
    this.setState({ isEditMode: true });
  }

  cancelEdit() {
    const { user } = this.props;
    const updatedUser = this.props.pageProps?.user || user;

    this.setState({
      isEditMode: false,
      username: updatedUser.username || '',
      email: updatedUser.email || '',
      bio: updatedUser.bio || '',
      avatarPreview: updatedUser.avatar_url || null,
      postSeoPreview: updatedUser.post_seo_image_url || null,
      role_ids: updatedUser.role_ids || [],
      errors: {},
    });
  }

  renderViewMode() {
    const { user } = this.props;
    const updatedUser = this.props.pageProps?.user || user;

    return (
      <Container size="md" px="0">
        <Paper radius="md" p="xl" shadow="sm" style={{ backgroundColor: '#757575' }}>
          <Group justify="space-between" mb="lg">
            <Title order={2} className="text-white">
              Account Information
            </Title>
            <Button leftSection={<IconEdit size={16} />} onClick={this.enableEditMode} variant="filled">
              Edit Account
            </Button>
          </Group>
          <Grid>
            <Grid.Col span={4}>
              <Stack align="center" gap="md">
                <Avatar src={updatedUser.avatar_url} size={120} radius={60} sx={{ border: '1px solid #ddd' }} />
                <Text fw={700} size="lg">
                  {updatedUser.name || updatedUser.username}
                </Text>
              </Stack>
            </Grid.Col>
            <Grid.Col span={8}>
              <Stack gap="xs">
                <Box>
                  <Text fw={800} size="16px" color="orange">
                    Username
                  </Text>
                  <Text size="md">{updatedUser.username}</Text>
                </Box>
                <Box>
                  <Text fw={800} size="sm" color="dimmed">
                    Email
                  </Text>
                  <Text size="md">{updatedUser.email}</Text>
                </Box>
                {updatedUser.bio && (
                  <Box>
                    <Text fw={500} size="sm" color="dimmed">
                      Bio
                    </Text>
                    <Text size="md">{updatedUser.bio}</Text>
                  </Box>
                )}
                <Box>
                  <Text fw={800} size="sm" color="dimmed">
                    Roles
                  </Text>
                  <Group gap="xs">
                    {updatedUser.role_ids && updatedUser.role_ids.length > 0 ? (
                      updatedUser.role_ids.map((role, index) => (
                        <Badge key={index} size="md" variant="filled">
                          {role}
                        </Badge>
                      ))
                    ) : (
                      <Badge size="lg" variant="outline">
                        User
                      </Badge>
                    )}
                  </Group>
                </Box>
              </Stack>
            </Grid.Col>
          </Grid>
        </Paper>
      </Container>
    );
  }

  renderEditMode() {
    const { available_roles = [] } = this.props;
    const { username, email, bio, role_ids, avatarPreview, processing, errors } = this.state;

    return (
      <Container size="md" px="0">
        <Paper radius="md" p="xl" shadow="sm" style={{ backgroundColor: '#757575' }}>
          <Group justify="space-between" mb="lg">
            <Title className="text-white" order={2}>
              Edit Account
            </Title>
            <Button
              leftSection={<IconArrowBack size={16} />}
              onClick={this.cancelEdit}
              color="rgba(194, 0, 0, 1)"
              variant="light"
            >
              Cancel
            </Button>
          </Group>
          <form onSubmit={this.handleSubmit}>
            <Grid>
              <Grid.Col span={12} mb="md">
                <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 20 }}>
                  {avatarPreview && (
                    <Avatar src={avatarPreview} size={120} radius={60} sx={{ border: '1px solid #ddd' }} />
                  )}
                  <ImageUploadModal previewUrl={avatarPreview} onImageChange={this.handleAvatarChange} />
                  {errors.avatar && (
                    <Text color="red" size="sm" mt={5}>
                      {errors.avatar}
                    </Text>
                  )}
                </Box>
              </Grid.Col>
              <Grid.Col span={12}>
                <TextInput
                  label="Username"
                  value={username}
                  onChange={this.handleUsernameChange}
                  required
                  error={errors.username}
                  style={{ color: '#f2f2f2' }}
                  disabled={processing}
                  mb="md"
                />
                <TextInput
                  label="Email"
                  value={email}
                  onChange={this.handleEmailChange}
                  required
                  style={{ color: '#f2f2f2' }}
                  error={errors.email}
                  disabled={processing}
                  mb="md"
                />
                <Textarea
                  label="Bio"
                  value={bio}
                  onChange={this.handleBioChange}
                  error={errors.bio}
                  disabled={processing}
                  mb="md"
                  autosize
                  minRows={3}
                />
                <MultiSelect
                  data={available_roles}
                  label="Roles"
                  searchable
                  hidePickedOptions
                  placeholder={role_ids.length === 0 ? 'Select roles' : ''}
                  value={role_ids}
                  onChange={this.handleRoleChange}
                  error={errors.role_ids}
                  mb="md"
                />
                <Group gap="right" mt="xl">
                  <Button type="button" color="rgba(194, 0, 0, 1)" variant="light" onClick={this.cancelEdit}>
                    Cancel
                  </Button>
                  <Button type="submit" loading={processing}>
                    Save Changes
                  </Button>
                </Group>
              </Grid.Col>
            </Grid>
          </form>
        </Paper>
      </Container>
    );
  }

  render() {
    const { isEditMode } = this.state;
    return (
      <>
        <Notifications />
        {isEditMode ? this.renderEditMode() : this.renderViewMode()}
      </>
    );
  }
}

export default UserAccountComponent;
